import * as React from 'react'

import { type Colors } from '@factorialco/deprecated-design-system'
import { BarDatum, ComputedDatum } from '@nivo/bar'

// Import the tooltip components - these should be available from the parent directory
import BarChartTooltip, {
  SmartBarChartTooltip,
} from 'modules/core/components/BarChart/BarChartTooltip'
import { generateTooltipData } from './tooltipHelpers'
import type { CustomTooltipProps, OriginalDataItem, TooltipData } from './types'

interface UseBarEventHandlersProps<RawDatum extends BarDatum> {
  bar: BarDatum
  data?: ComputedDatum<RawDatum>
  stackId: string
  isInteractive: boolean
  groupMode: 'grouped' | 'stacked'
  originalData: OriginalDataItem[]
  keys: string[]
  colors: Colors[]
  color: unknown
  customTooltip?: React.FC<CustomTooltipProps>
  onClick?: (
    datum: ComputedDatum<RawDatum> & {
      color: string
    },
    event: React.MouseEvent<SVGRectElement, MouseEvent>
  ) => void
  onMouseEnter?: (
    datum: ComputedDatum<RawDatum> & {
      color: string
    },
    event: React.MouseEvent<SVGRectElement, MouseEvent>
  ) => void
  onMouseLeave?: (
    datum: ComputedDatum<RawDatum> & {
      color: string
    },
    event: React.MouseEvent<SVGRectElement, MouseEvent>
  ) => void
  toggleSelection: (id: string) => void
  setHoveredStackIndex: (idx: string | null) => void
  showTooltipFromEvent: (element: React.ReactElement, event: React.MouseEvent) => void
  hideTooltip: () => void
}

export const useBarEventHandlers = <RawDatum extends BarDatum>({
  bar,
  data,
  stackId,
  isInteractive,
  groupMode,
  originalData,
  keys,
  colors,
  color,
  customTooltip,
  onClick,
  onMouseEnter,
  onMouseLeave,
  toggleSelection,
  setHoveredStackIndex,
  showTooltipFromEvent,
  hideTooltip,
}: UseBarEventHandlersProps<RawDatum>) => {
  const getTooltipData = React.useCallback((): TooltipData | null => {
    if (!data) return null
    return generateTooltipData(
      groupMode,
      data.data,
      stackId,
      bar,
      originalData,
      keys,
      colors,
      color
    )
  }, [groupMode, data, stackId, bar, originalData, keys, colors, color])

  const handleClick = React.useCallback(
    (evt: React.MouseEvent<SVGRectElement, MouseEvent>) => {
      if (!bar || !data) return
      toggleSelection(stackId)
      onClick?.({ ...data, color: String(bar.color) }, evt)
    },
    [bar, data, onClick, toggleSelection, stackId]
  )

  const handleMouseEnter = React.useCallback(
    (evt: React.MouseEvent<SVGRectElement, MouseEvent>) => {
      if (!data) return
      onMouseEnter?.({ ...data, color: String(bar.color) }, evt)
      setHoveredStackIndex(stackId)

      if (isInteractive) {
        if (customTooltip) {
          const tooltipData = getTooltipData()
          if (!tooltipData) return
          const value = tooltipData.value || 0
          const keyIndex = data ? keys.indexOf(String(data.id)) : -1
          const colorName = keyIndex !== -1 ? colors[keyIndex % colors.length] : undefined
          if (colorName) {
            const tooltipElement = React.createElement(customTooltip, {
              label: stackId,
              value,
              color: colorName,
              id: stackId,
              index: typeof bar?.index === 'number' ? bar.index : 0,
            })
            showTooltipFromEvent(tooltipElement, evt)
          }
        } else {
          const tooltipData = getTooltipData()
          if (!tooltipData) return
          const tooltipElement = React.createElement(SmartBarChartTooltip, {
            data: tooltipData,
          })
          showTooltipFromEvent(tooltipElement, evt)
        }
      }
    },
    [
      data,
      onMouseEnter,
      setHoveredStackIndex,
      stackId,
      isInteractive,
      customTooltip,
      getTooltipData,
      showTooltipFromEvent,
      bar,
      keys,
      colors,
    ]
  )

  const handleMouseMove = React.useCallback(
    (evt: React.MouseEvent<SVGRectElement, MouseEvent>) => {
      if (!isInteractive || !data) return

      const tooltipData = getTooltipData()
      if (tooltipData === null) return

      if (customTooltip) {
        const value = tooltipData.value || 0
        const keyIndex = data ? keys.indexOf(String(data.id)) : -1
        const colorName = keyIndex !== -1 ? colors[keyIndex % colors.length] : undefined

        if (colorName) {
          const tooltipElement = React.createElement(customTooltip, {
            label: stackId,
            value,
            color: colorName,
            id: stackId,
            index: typeof bar?.index === 'number' ? bar.index : 0,
          })
          showTooltipFromEvent(tooltipElement, evt)
        }
      } else {
        const tooltipElement = React.createElement(BarChartTooltip, {
          data: tooltipData,
        })
        showTooltipFromEvent(tooltipElement, evt)
      }
    },
    [
      isInteractive,
      customTooltip,
      getTooltipData,
      showTooltipFromEvent,
      stackId,
      bar,
      data,
      keys,
      colors,
    ]
  )

  const handleMouseLeave = React.useCallback(
    (evt: React.MouseEvent<SVGRectElement, MouseEvent>) => {
      if (!data) return
      onMouseLeave?.({ ...data, color: String(bar.color) }, evt)
      setHoveredStackIndex(null)
      if (isInteractive) {
        hideTooltip()
      }
    },
    [data, onMouseLeave, setHoveredStackIndex, isInteractive, hideTooltip, bar]
  )

  return {
    handleClick,
    handleMouseEnter,
    handleMouseMove,
    handleMouseLeave,
  }
}
